// src/pages/motolivraison/livreurs.page.jsx
//
// Liste de tous les livreurs MotoLivraison avec filtres et actions admin.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Modal, message, Button, Card, Row, Col, Tag, Tooltip, Space,
  Breadcrumb, Statistic, Avatar, Select, Dropdown, Form, Input
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, EyeOutlined, PlayCircleOutlined,
  CarOutlined, RightOutlined, DownOutlined, ClockCircleOutlined,
  UserOutlined, UserAddOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const Livreurs = () => {
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '' });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const stats = {
    total: livreurs.length,
    pending: livreurs.filter(l => l.livreurMotoProfile?.status === 'pending').length,
    approved: livreurs.filter(l => l.livreurMotoProfile?.status === 'approved').length,
    actifs: livreurs.filter(l => l.livreurMotoProfile?.status === 'approved' && l.livreurMotoProfile?.isAvailable).length,
  };

  const fetchLivreurs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listLivreurs({
        status: filters.status || undefined,
      });
      setLivreurs(data?.livreurs || []);
    } catch (e) {
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLivreurs();
  }, [fetchLivreurs]);

  const handleAction = async (action, livreur) => {
    Modal.confirm({
      title: `${action === 'validate' ? 'Approuver' : action === 'suspend' ? 'Suspendre' : 'Réactiver'} ce livreur ?`,
      content: `${livreur.firstName} ${livreur.lastName} (${livreur.phone})`,
      onOk: async () => {
        try {
          if (action === 'validate') await motolivraisonService.validateLivreur(livreur._id);
          else if (action === 'suspend') await motolivraisonService.suspendLivreur(livreur._id);
          else if (action === 'reactivate') await motolivraisonService.reactivateLivreur(livreur._id);
          message.success('Action effectuée');
          fetchLivreurs();
        } catch (e) {
          message.error(e?.message || 'Erreur');
        }
      },
    });
  };

  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ cityCode: 'spd' });
    setCreateModalOpen(true);
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const res = await motolivraisonService.createLivreurByAdmin(values);
      const successMsg = res?.userCreated
        ? `Livreur créé. Mot de passe par défaut : 123456`
        : (res?.profileAlreadyExists
            ? 'Cet utilisateur est déjà livreur approuvé'
            : 'Profil livreur ajouté à un utilisateur existant');
      message.success(successMsg);
      setCreateModalOpen(false);
      fetchLivreurs();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  const columns = [
    {
      title: 'Livreur',
      key: 'livreur',
      render: (_, r) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Ville',
      key: 'ville',
      render: (_, r) => r.livreurMotoProfile?.cityId?.name || '—',
    },
    {
      title: 'Statut',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const s = r.livreurMotoProfile?.status;
        return <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: 'Disponibilité',
      key: 'dispo',
      width: 130,
      align: 'center',
      render: (_, r) => {
        if (r.livreurMotoProfile?.status !== 'approved') return '—';
        const isAvailable = r.livreurMotoProfile?.isAvailable;
        return (
          <Tag color={isAvailable ? 'green' : 'default'}>
            {isAvailable ? '🟢 Disponible' : '⚫ Indisponible'}
          </Tag>
        );
      },
    },
    {
      title: 'Missions',
      key: 'missions',
      width: 100,
      align: 'center',
      render: (_, r) => r.livreurMotoProfile?.stats?.missionsCompletees || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, r) => {
        const items = [];
        const status = r.livreurMotoProfile?.status;
        if (status === 'pending') {
          items.push({
            key: 'validate',
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
            label: 'Approuver',
            onClick: () => handleAction('validate', r),
          });
        }
        if (status === 'approved') {
          items.push({
            key: 'suspend',
            icon: <StopOutlined style={{ color: 'red' }} />,
            label: 'Suspendre',
            onClick: () => handleAction('suspend', r),
          });
        }
        if (status === 'suspended') {
          items.push({
            key: 'reactivate',
            icon: <PlayCircleOutlined style={{ color: 'green' }} />,
            label: 'Réactiver',
            onClick: () => handleAction('reactivate', r),
          });
        }
        return (
          <Space>
            <Tooltip title="Voir détail">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/dashboard/motolivraison/livreurs/${r._id}`)}
              />
            </Tooltip>
            {items.length > 0 && (
              <Dropdown menu={{ items }}>
                <Button size="small">Actions <DownOutlined /></Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Livreurs</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}><Card><Statistic title="Total" value={stats.total} prefix={<CarOutlined />} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En attente" value={stats.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Approuvés" value={stats.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En ligne" value={stats.actifs} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        </Row>

        <Card
          title="Livreurs MotoLivraison"
          extra={
            <Button type="primary" icon={<UserAddOutlined />} onClick={openCreate}>
              Nouveau livreur
            </Button>
          }
        >
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Select
                placeholder="Filtrer par statut"
                allowClear
                style={{ width: '100%' }}
                value={filters.status || undefined}
                onChange={(v) => setFilters({ ...filters, status: v || '' })}
                options={[
                  { value: 'pending', label: 'En attente' },
                  { value: 'approved', label: 'Approuvés' },
                  { value: 'suspended', label: 'Suspendus' },
                ]}
              />
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={livreurs.map(l => ({ ...l, key: l._id }))}
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 900 }}
          />
        </Card>
      </div>

      {/* Modal Create Livreur */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8 }} />
            Nouveau livreur MotoLivraison
          </span>
        }
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        okText="Créer"
        cancelText="Annuler"
        confirmLoading={creating}
        width={560}
      >
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
          }}
        >
          <strong>ℹ️ Mot de passe par défaut :</strong>{' '}
          <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4 }}>123456</code>
          <br />
          <small style={{ color: '#8c8c8c' }}>
            Le livreur pourra le changer après sa première connexion. Si le numéro existe déjà,
            le profil livreur sera ajouté à l'utilisateur existant (sans modifier son mot de passe).
          </small>
        </div>
        <Form form={createForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[{ required: true, message: 'Prénom requis' }]}
              >
                <Input placeholder="Ex: Yao" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Nom">
                <Input placeholder="Ex: Konan" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="phone"
            label="Téléphone"
            rules={[{ required: true, message: 'Téléphone requis' }]}
            extra="Format : +225XXXXXXXX"
          >
            <Input placeholder="+225..." />
          </Form.Item>
          <Form.Item name="email" label="Email (optionnel)">
            <Input type="email" placeholder="email@exemple.com" />
          </Form.Item>
          <Form.Item
            name="cityCode"
            label="Ville"
            rules={[{ required: true, message: 'Ville requise' }]}
          >
            <Select
              options={[
                { value: 'spd', label: 'San Pedro' },
                { value: 'abj', label: 'Abidjan' },
              ]}
            />
          </Form.Item>
          <Form.Item name="numeroPermisConduire" label="Numéro de permis de conduire (optionnel)">
            <Input placeholder="Ex: 1234567890" />
          </Form.Item>
          <Form.Item name="plaqueImmatriculation" label="Plaque d'immatriculation (optionnel)">
            <Input placeholder="Ex: AA-123-CI" />
          </Form.Item>
          <Form.Item name="couleurMoto" label="Couleur de la moto (optionnel)">
            <Input placeholder="Ex: Rouge" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Livreurs;
